<template>
    <div>
        <p v-if="loading" class="text-muted text-center"><i class="fas fa-spinner fa-spin"></i> Chargement…</p>
        <p v-if="error" class="text-danger"><i class="fas fa-exclamation-circle"></i> {{ error }}</p>
        <p v-if="!loading && !error && rows.length === 0" class="text-muted">Aucun résultat.</p>

        <!-- Toggle tableau / graphique -->
        <div v-if="chart_configs.length > 0 && rows.length > 0" class="table-view-toggle">
            <b-button-group size="sm">
                <b-button :variant="view_mode === 'table' ? 'primary' : 'outline-secondary'" @click="switchView('table')">
                    <i class="fas fa-table"></i> Tableau
                </b-button>
                <b-button :variant="view_mode === 'chart' ? 'primary' : 'outline-secondary'" @click="switchView('chart')">
                    <i class="fas fa-chart-pie"></i> Graphique
                </b-button>
            </b-button-group>
        </div>

        <!-- Tabulator : toujours dans le DOM pour que Tabulator.js s'initialise correctement.
             Masqué sans display:none quand graphique actif. -->
        <div :class="{ 'table-hidden-no-display': chart_configs.length > 0 && view_mode === 'chart' }">
            <edit_tabulator
                :extern_file="false"
                :row_updates="false"
                :default_visible="true"
                v-show="rows.length > 0"
                ref="tabulator">
            </edit_tabulator>
        </div>

        <!-- Graphiques (v-if : ChartDisplay monte/démonte automatiquement) -->
        <div v-if="chart_configs.length > 0 && view_mode === 'chart' && rows.length > 0">
            <template v-if="chart_configs.length > 1">
                <b-tabs content-class="pt-2">
                    <b-tab v-for="(cfg, i) in chart_configs" :key="i"
                        :title="cfg.title || `Graphique ${i + 1}`">
                <chart-display :rows="rows" :config="cfg" :editable="true" :table-name="tableName" />
                    </b-tab>
                </b-tabs>
            </template>
            <chart-display v-else
                :rows="rows" :config="chart_configs[0]" :editable="true" :table-name="tableName" />
        </div>
    </div>
</template>

<script>
import edit_tabulator from "../edit_tabulator.vue";
import ChartDisplay from "../ChartDisplay.vue";
import database_services from "../../services/database.js";

export default {
    components: { edit_tabulator, ChartDisplay },
    props: {
        format:      { default: "small" },
        init_params: {},
        test_jwt:    { default: null }
    },
    data() {
        return {
            rows:         [],
            loading:      false,
            error:        null,
            tableName:      null,
            chart_configs:  [],
            view_mode:      'table'
        };
    },
    methods: {
        switchView(mode) {
            this.view_mode = mode;
        },
        async load(data) {
            if (!data || !data.table) return;

            this.loading   = true;
            this.error     = null;
            this.rows      = [];
            this.tableName = 'table - ' + data.table;

            try {
                const config = data.config || {};
                const table  = config.table || data.table;
                const select = config.select || "*";

                let end = "";
                if (config.rules_sql) end += " " + config.rules_sql;
                // "no limit" et "100 derniers" sont des flags internes (comme dans database.vue),
                // pas des fragments SQL valides — ne jamais les injecter dans la requête.
                if (config.event && config.event !== "no limit" && config.event !== "100 derniers") {
                    end += " " + config.event;
                }
                // Par défaut LIMIT 100, sauf si le flag "no limit" est explicitement positionné
                if (!config.event || config.event === "100 derniers") {
                    end += " LIMIT 100";
                }

                const result = await database_services.get("select", table, {
                    select,
                    end: end.trim()
                }, this.test_jwt);

                this.rows = Array.isArray(result) ? result : [];

                // Config graphique
                const chartData = data.chart;
                if (chartData && chartData.enable) {
                    if (chartData.charts && chartData.charts.length) {
                        // Format actuel
                        this.chart_configs = chartData.charts;
                    } else {
                        // Ancien format plat { type, data_mode, ... }
                        this.chart_configs = [{
                            type:            chartData.type            || 'pie',
                            data_mode:       chartData.data_mode       || 'count',
                            label_col:       chartData.label_col       || '',
                            date_trunc:      chartData.date_trunc      || false,
                            date_trunc_unit: chartData.date_trunc_unit || 'day',
                            value_col:       chartData.value_col       || '',
                            series_col:      chartData.series_col      || '',
                            title:           chartData.title           || ''
                        }];
                    }
                    this.view_mode = chartData.default_view || 'table';
                } else {
                    this.chart_configs = [];
                    this.view_mode = 'table';
                }

                await this.$nextTick();

                if (this.$refs.tabulator && this.rows.length > 0) {
                    this.$refs.tabulator.set_data(this.rows);
                }
                // Vue mettra à jour chart-display automatiquement via v-if + watch(rows)
            } catch (err) {
                console.error("[table.vue] Erreur chargement requête", err);
                this.error = "Erreur lors du chargement des données.";
            } finally {
                this.loading = false;
            }
        }
    },
    mounted() {
        if (this.init_params) {
            this.load(this.init_params);
        }
    }
};
</script>
<style scoped>
.table-view-toggle {
    margin-bottom: 10px;
}
/* Masque sans display:none pour ne pas casser Tabulator.js */
.table-hidden-no-display {
    visibility: hidden;
    height: 0;
    overflow: hidden;
}
</style>